<template>
  <div>
    <NavBar />
    <div class="container">
      <div class="row">
        <div class="col">
          <h1>Organizations <small>(All)</small></h1>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <div class="d-flex py-4">
            <form class="form-inline" @submit.prevent>
              <div class="ml-auto">
                <div class="form-group">
                  <input type="text" class="form-control mr-2" placeholder="Search" v-model="query" />
                  <button type="button" class="btn btn-primary mr-2" @click="search">
                    <fa icon="search" />
                    <span class="ml-2">Search</span>
                  </button>
                  <button type="button" class="btn btn-primary" @click="refresh">
                    <fa icon="redo" />
                    <span class="ml-2">Refresh</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
        <div class="col-md-auto">
          <div class="d-flex py-4">
            <div class="ml-auto">
              <button type="button" class="btn btn-primary" @click="create">
                <fa icon="plus" />
                <span class="ml-2">Add Organization</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <div class="table-responsive">
            <table class="table table-hover">
              <thead class="thead-light">
                <tr>
                  <th scope="col">Legal Name</th>
                  <th scope="col">Display Name</th>
                  <th scope="col" class="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="isLoading" class="text-center text-muted">
                  <td colspan="12"><fa icon="circle-notch" spin /> Loading...</td>
                </tr>
                <tr v-if="error" class="text-center text-muted">
                  <td colspan="12">Sorry. There was an error. Please try again.</td>
                </tr>
                <tr v-if="page.total === 0" class="text-center text-muted">
                  <td colspan="12">No results</td>
                </tr>
                <tr v-for="o in page.items" :key="o.id">
                  <td @click="edit(o)" scope="row">
                    <span>{{ o.legalName }}</span>
                  </td>
                  <td @click="edit(o)">
                    <span>{{ o.displayName }}</span>
                  </td>
                  <td>
                    <div class="btn-group d-flex justify-content-center" role="group">
                      <button type="button" class="btn btn-link" @click="viewUsers(o)">View Users</button>
                      <button type="button" class="btn btn-link" @click="viewReports(o)">View Reports</button>
                      <button type="button" class="btn btn-link" @click="viewInvestigations(o)">View Investigations</button>
                      <button type="button" class="btn btn-link" @click="edit(o)">Edit</button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div v-if="needsPaging" class="row">
        <div class="col">
          <div class="row justify-content-between p-3">
            <button type="button" class="btn btn-outline-dark" @click="prev" :disabled="!hasPrev">Previous</button>
            <button type="button" class="btn btn-outline-dark" @click="next" :disabled="!hasNext">Next</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import debounce from "debounce";
import NavBar from "@/views/reports/NavBar.vue";

export default {
  components: {
    NavBar,
  },
  data() {
    return {
      isLoading: false,
      skip: 0,
      take: 25,
      query: null,

      page: {},
      error: null,
    };
  },
  computed: {
    ...mapGetters("organization", ["organization"]),
    ...mapGetters("user", ["user"]),
    hasPrev() {
      if (!this.page) return false;
      return this.skip > 0;
    },
    hasNext() {
      if (!this.page) return false;
      return this.skip + this.take < this.page.total;
    },
    needsPaging() {
      return this.hasNext || this.hasPrev;
    },
    url() {
      let url = `/v1/organizations?skip=${this.skip}&take=${this.take}`;
      url += this.query ? `&query=${encodeURIComponent(this.query)}` : "";
      return url;
    },
  },
  watch: {
    skip() {
      this.load();
    },
    take() {
      this.load();
    },
    query() {
      this.skip = 0;
      this.load();
    },
    user() {
      this.skip = 0;
      this.load();
    },
    organization() {
      this.skip = 0;
      this.load();
    },
  },
  created() {
    this.load = debounce(this.load, 350);
    this.load();
  },
  methods: {
    load() {
      this.page = {};
      this.error = null;
      this.isLoading = true;
      this.$axios
        .get(this.url)
        .then(res => (this.page = res.data))
        .catch(err => (this.error = err))
        .finally(() => (this.isLoading = false));
    },
    prev() {
      this.skip = Math.max(0, this.skip - this.take);
    },
    next() {
      this.skip += this.take;
    },
    create() {
      this.$router.push(`/organizations/create`);
    },
    edit(organization) {
      this.$router.push(`/organizations/${organization.id}`);
    },
    viewUsers(organization) {
      this.$router.push(`/organizations/${organization.id}/users`);
    },
    viewReports(organization) {
      this.$router.push(`/organizations/${organization.id}/reports`);
    },
    viewInvestigations(organization) {
      this.$router.push(`/organizations/${organization.id}/investigations`);
    },
    search() {
      this.load();
    },
    refresh() {
      this.load();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/base.scss";

table {
  tr {
    td {
      span {
        min-width: 250px;
        max-width: 400px;
        @extend .text-nowrap;
        @extend .overflow-hidden;
        @extend .text-truncate;
        @extend .d-inline-block;
      }
    }
  }
}

table.table-hover tbody tr {
  cursor: pointer;
}
</style>
